import { createContext, useContext, useState, useEffect, type ReactNode } from "react";
import type { User, AuthError } from "@supabase/supabase-js";
import { supabase, type Role } from "@/lib/supabase";

interface AuthContextType {
  user: User | null;
  role: Role | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<{ error: string | null }>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  role: null,
  isLoading: true,
  isAuthenticated: false,
  login: async () => ({ error: null }),
  logout: async () => {},
});

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState<Role | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check for existing session
    const checkSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();

        // If the stored token is invalid/expired, clear it and sign out cleanly
        if (error) {
          await supabase.auth.signOut();
          setUser(null);
          setRole(null);
          setIsLoading(false);
          return;
        }

        if (session?.user) {
          setUser(session.user);
          // Fetch user profile
          const { data: profile } = await supabase
            .from('profiles')
            .select('role, status')
            .eq('id', session.user.id)
            .maybeSingle();

          if (profile?.status === 'inactive') {
            await supabase.auth.signOut();
            setUser(null);
            setRole(null);
          } else {
            setRole(profile?.role as Role || 'editor');
          }
        }
      } catch {
        // Any unexpected auth error — clear the session
        try { await supabase.auth.signOut(); } catch { /* ignore */ }
        setUser(null);
        setRole(null);
      }
      setIsLoading(false);
    };

    checkSession();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        // TOKEN_REFRESHED failure surfaces as a new SIGNED_OUT or as an error event
        if (event === 'TOKEN_REFRESHED' && !session) {
          setUser(null);
          setRole(null);
          setIsLoading(false);
          return;
        }
        if (event === 'SIGNED_IN' && session?.user) {
          setUser(session.user);
          const { data: profile } = await supabase
            .from('profiles')
            .select('role, status')
            .eq('id', session.user.id)
            .maybeSingle();

          if (profile?.status === 'inactive') {
            await supabase.auth.signOut();
            setUser(null);
            setRole(null);
          } else {
            setRole(profile?.role as Role || 'editor');
            // Update last_login
            await supabase
              .from('profiles')
              .update({ last_login: new Date().toISOString() })
              .eq('id', session.user.id);
          }
        } else if (event === 'SIGNED_OUT') {
          setUser(null);
          setRole(null);
        }
        setIsLoading(false);
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const login = async (email: string, password: string): Promise<{ error: string | null }> => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email: email.trim().toLowerCase(),
      password,
    });

    if (error) {
      return { error: error.message };
    }

    if (data.user) {
      // Check profile status
      const { data: profile } = await supabase
        .from('profiles')
        .select('role, status')
        .eq('id', data.user.id)
        .maybeSingle();

      if (profile?.status === 'inactive') {
        await supabase.auth.signOut();
        return { error: 'Your account has been deactivated. Contact the administrator.' };
      }

      setUser(data.user);
      setRole(profile?.role as Role || 'editor');

      // Update last_login
      await supabase
        .from('profiles')
        .update({ last_login: new Date().toISOString() })
        .eq('id', data.user.id);
    }

    return { error: null };
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setRole(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        role,
        isLoading,
        isAuthenticated: !!user,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}